/**
 * Consumption / usage-tier logic for the meter-first flow (SHOP-4 addition).
 *
 * The shopper picks how much electricity they use — **monthly kWh** — after their
 * meter resolves. The choice is either a coarse TIER (Low / Medium / High) or a
 * CUSTOM monthly figure, and it is the single value SHOP-5A prices plans at.
 *
 * ALL logic lives here (CLAUDE.md — nothing in the `.tsx`): tier definitions,
 * annual→monthly conversion, the tier a monthly figure falls in, the auto-select
 * on mount, and custom validation.
 *
 * ── BOUNDARY RULES (explicit) ──────────────────────────────────────────────
 *   0  <  m  ≤  500   → Low
 *   500 <  m  ≤ 1000  → Medium
 *   1000 < m  ≤ 2000  → High
 *   m  > 2000  (or m ≤ 0) → no tier → Custom
 * A tier's REPRESENTATIVE monthly kWh is its upper bound (500 / 1000 / 2000) —
 * exactly the three EFL reference levels — so a tier prices at an exact EFL point
 * and only a custom value interpolates (see planPricing.ts).
 */

export type UsageTierId = 'low' | 'medium' | 'high';

export interface UsageTier {
  readonly id: UsageTierId;
  /** i18n key for the radio label (includes the range as copy). */
  readonly nameKey: string;
  /** Exclusive lower bound (Low starts at 0). */
  readonly min: number;
  /** Inclusive upper bound; also the tier's representative monthly kWh (an EFL level). */
  readonly max: number;
}

export const USAGE_TIERS: readonly UsageTier[] = [
  { id: 'low', nameKey: 'shop.consumption.tier.low', min: 0, max: 500 },
  { id: 'medium', nameKey: 'shop.consumption.tier.medium', min: 500, max: 1000 },
  { id: 'high', nameKey: 'shop.consumption.tier.high', min: 1000, max: 2000 },
] as const;

/** The token stored for a Custom selection (not a real tier). */
export const CUSTOM_TIER_ID = 'custom' as const;
export type ConsumptionSelectionId = UsageTierId | typeof CUSTOM_TIER_ID;

/** Annualised usage → whole monthly kWh (÷12, rounded). */
export function annualToMonthly(annual: number): number {
  if (!Number.isFinite(annual) || annual <= 0) return 0;
  return Math.round(annual / 12);
}

/** The tier a monthly figure falls in, or null when it is >2000 (or ≤0). */
export function tierForMonthly(monthly: number): UsageTier | null {
  return USAGE_TIERS.find((tier) => monthly > tier.min && monthly <= tier.max) ?? null;
}

/** The representative monthly kWh a tier prices at (its upper bound / EFL level). */
export function tierRepresentativeKwh(tier: UsageTier): number {
  return tier.max;
}

export interface InitialSelection {
  /** The auto-selected tier, or null when the meter usage is above 2000/mo. */
  readonly tier: UsageTier | null;
  /** The monthly kWh to store (tier representative, or the raw monthly for custom). */
  readonly monthlyKwh: number;
  /** True when the meter usage falls outside the tiers → Custom pre-filled. */
  readonly isCustom: boolean;
}

/**
 * Auto-select on mount from the meter's estimated ANNUAL usage. Falls in a tier →
 * that tier (stored at its representative kWh). Above 2000/mo → Custom pre-filled
 * with the (rounded) monthly value.
 */
export function resolveInitialSelection(estimatedAnnualUsageKwh: number): InitialSelection {
  const monthly = annualToMonthly(estimatedAnnualUsageKwh);
  const tier = tierForMonthly(monthly);
  if (tier !== null) {
    return { tier, monthlyKwh: tierRepresentativeKwh(tier), isCustom: false };
  }
  return { tier: null, monthlyKwh: monthly, isCustom: true };
}

/** True when a raw custom-input string is a valid positive integer monthly kWh. */
export function isValidCustomKwh(raw: string): boolean {
  return parseCustomKwh(raw) !== null;
}

/** Parse a custom-input string to a positive integer, or null when invalid. */
export function parseCustomKwh(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/u.test(trimmed)) return null;
  const value = Number.parseInt(trimmed, 10);
  return value > 0 ? value : null;
}
